// v1.28.83 — Highest Earning Parties (Top Parties of the Month) report.
// Adds a summary banner above the table, a bold TOTAL row at the bottom,
// and an Excel download button next to the existing PDF download.

import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Trophy, DownloadSimple, ArrowClockwise, FileXls, CalendarBlank } from "@phosphor-icons/react";
import { withDatePrefix } from "@/lib/downloadFilename";

const inr = (n) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 2 }).format(Number(n || 0));

const MONTHS = [
    "January","February","March","April","May","June",
    "July","August","September","October","November","December",
];

const currentYM = () => {
    const d = new Date();
    return { year: d.getFullYear(), month: d.getMonth() + 1 };
};

const yearOptions = () => {
    const now = new Date().getFullYear();
    return [now, now - 1, now - 2, now - 3];
};

const daysInMonth = (year, month) => new Date(year, month, 0).getDate();

export default function TopParties() {
    const init = currentYM();
    const [year, setYear] = useState(init.year);
    const [month, setMonth] = useState(init.month);
    const [sort, setSort] = useState("amount");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [downloadingPdf, setDownloadingPdf] = useState(false);
    const [downloadingXlsx, setDownloadingXlsx] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data: d } = await api.get("/reports/top-parties", { params: { year, month, sort, limit: 200 } });
            setData(d);
        } catch (err) {
            toast.error(err?.message || "Failed to load top parties");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, month, sort]);

    const rows = data?.rows || [];
    const totals = useMemo(() => {
        return rows.reduce((acc, r) => ({
            total_received: acc.total_received + Number(r.total_received || 0),
            commission: acc.commission + Number(r.commission || 0),
            portal_fee: acc.portal_fee + Number(r.portal_fee || 0),
            net_amount: acc.net_amount + Number(r.net_amount || 0),
            txn_count: acc.txn_count + Number(r.txn_count || 0),
        }), { total_received: 0, commission: 0, portal_fee: 0, net_amount: 0, txn_count: 0 });
    }, [rows]);

    const dateRangeLabel = useMemo(() => {
        const d1 = `01 ${MONTHS[month - 1].slice(0,3)} ${year}`;
        const d2 = `${daysInMonth(year, month)} ${MONTHS[month - 1].slice(0,3)} ${year}`;
        return `${d1} → ${d2}`;
    }, [year, month]);

    const doDownload = async (kind) => {
        const setter = kind === "xlsx" ? setDownloadingXlsx : setDownloadingPdf;
        setter(true);
        try {
            const res = await api.get(`/export/top-parties.${kind}`, {
                params: { year, month, sort, limit: 200 }, responseType: "blob",
            });
            const type = kind === "xlsx"
                ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                : "application/pdf";
            const blob = new Blob([res.data], { type });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = withDatePrefix(`top-parties-${year}-${String(month).padStart(2, "0")}.${kind}`);
            document.body.appendChild(a); a.click(); a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            toast.error(err?.message || `Failed to download ${kind.toUpperCase()}`);
        } finally { setter(false); }
    };

    return (
        <div className="space-y-4" data-testid="top-parties-page">
            <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div className="flex items-center gap-3">
                        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow">
                            <Trophy size={20} weight="fill" />
                        </span>
                        <div>
                            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-700">Report</div>
                            <h1 className="font-display text-xl font-black text-slate-900 sm:text-2xl">Highest Earning Parties</h1>
                            <div className="text-xs text-slate-500">Your own tenant only · isolated from other companies</div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">Month</label>
                            <select data-testid="top-parties-month" value={month} onChange={(e) => setMonth(Number(e.target.value))}
                                className="mt-1 rounded border border-slate-300 px-2 py-1 text-xs">
                                {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">Year</label>
                            <select data-testid="top-parties-year" value={year} onChange={(e) => setYear(Number(e.target.value))}
                                className="mt-1 rounded border border-slate-300 px-2 py-1 text-xs">
                                {yearOptions().map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-500">Rank By</label>
                            <select data-testid="top-parties-sort" value={sort} onChange={(e) => setSort(e.target.value)}
                                className="mt-1 rounded border border-slate-300 px-2 py-1 text-xs">
                                <option value="amount">Total Received</option>
                                <option value="count">Transaction Count</option>
                                <option value="commission">Commission Earned</option>
                            </select>
                        </div>
                        <button type="button" data-testid="top-parties-reload" onClick={load} disabled={loading}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-slate-900 px-3 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-50">
                            <ArrowClockwise size={13} weight="bold" /> {loading ? "…" : "Refresh"}
                        </button>
                        <button type="button" data-testid="top-parties-download-xlsx" onClick={() => doDownload("xlsx")} disabled={downloadingXlsx || loading || !data}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
                            <FileXls size={13} weight="bold" /> {downloadingXlsx ? "Preparing…" : "Download Excel"}
                        </button>
                        <button type="button" data-testid="top-parties-download-pdf" onClick={() => doDownload("pdf")} disabled={downloadingPdf || loading || !data}
                            className="inline-flex h-[30px] items-center gap-1 rounded bg-rose-700 px-3 text-xs font-semibold text-white hover:bg-rose-800 disabled:opacity-50">
                            <DownloadSimple size={13} weight="bold" /> {downloadingPdf ? "Preparing…" : "Download PDF"}
                        </button>
                    </div>
                </div>
            </div>

            {/* v1.28.83 — Summary banner. Aggregates every visible row so the
                totals stay accurate when the Month/Year/Rank-By filters change. */}
            <div data-testid="top-parties-summary-banner"
                 className="rounded-xl border border-violet-200 bg-gradient-to-r from-violet-50 via-white to-emerald-50 p-4 shadow-sm">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-violet-700">
                        <CalendarBlank size={14} weight="bold" />
                        <span data-testid="top-parties-summary-range">{dateRangeLabel}</span>
                        <span className="text-slate-300">·</span>
                        <span>{rows.length} part{rows.length === 1 ? "y" : "ies"}</span>
                    </div>
                </div>
                <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                    <SumCard label="Total Payments Received" value={totals.total_received} testId="summary-total-received" tint="emerald" />
                    <SumCard label="Total Commission Earned"  value={totals.commission}     testId="summary-total-commission" tint="violet" />
                    <SumCard label="Total Portal Fee"          value={totals.portal_fee}    testId="summary-total-portal-fee" tint="amber" />
                    <SumCard label="Total Combined Earnings"   value={totals.net_amount}    testId="summary-total-combined"   tint="slate" strong />
                </div>
            </div>

            <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="min-w-full text-xs" data-testid="top-parties-table">
                    <thead className="bg-slate-50">
                        <tr>
                            <th className="w-[60px] px-3 py-2 text-center font-bold uppercase tracking-wider text-slate-600">Rank</th>
                            <th className="px-3 py-2 text-left font-bold uppercase tracking-wider text-slate-600">Party Name</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Payments Received</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Commission</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Portal Fee</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Combined</th>
                            <th className="px-3 py-2 text-right font-bold uppercase tracking-wider text-slate-600">Txn Count</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 && !loading && (
                            <tr><td colSpan={7} className="px-3 py-8 text-center text-slate-400">No transactions in {MONTHS[month - 1]} {year}.</td></tr>
                        )}
                        {rows.map((r) => (
                            <tr key={r.party_id} data-testid={`top-parties-row-${r.party_id}`} className="border-t border-slate-100 hover:bg-slate-50">
                                <td className="px-3 py-2 text-center">
                                    <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-black ${
                                        r.rank === 1 ? "bg-amber-100 text-amber-700"
                                        : r.rank === 2 ? "bg-slate-100 text-slate-700"
                                        : r.rank === 3 ? "bg-orange-100 text-orange-700"
                                        : "bg-slate-50 text-slate-500"
                                    }`}>{r.rank}</span>
                                </td>
                                <td className="px-3 py-2 font-semibold text-slate-800">{r.party_name}</td>
                                <td className="px-3 py-2 text-right font-bold text-emerald-700 tabular-nums">{inr(r.total_received)}</td>
                                <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{inr(r.commission)}</td>
                                <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{inr(r.portal_fee)}</td>
                                <td className="px-3 py-2 text-right font-semibold text-slate-800 tabular-nums">{inr(r.net_amount)}</td>
                                <td className="px-3 py-2 text-right text-slate-700 tabular-nums">{r.txn_count}</td>
                            </tr>
                        ))}
                        {rows.length > 0 && (
                            <tr className="border-t-2 border-violet-400 bg-violet-50" data-testid="top-parties-totals">
                                <td className="px-3 py-2"></td>
                                <td className="px-3 py-2 text-[12px] font-black uppercase tracking-wider text-violet-900" data-testid="top-parties-total-label">TOTAL</td>
                                <td className="px-3 py-2 text-right font-black text-emerald-700 tabular-nums" data-testid="top-parties-total-received">{inr(totals.total_received)}</td>
                                <td className="px-3 py-2 text-right font-black text-slate-900 tabular-nums" data-testid="top-parties-total-commission">{inr(totals.commission)}</td>
                                <td className="px-3 py-2 text-right font-black text-slate-900 tabular-nums" data-testid="top-parties-total-portal-fee">{inr(totals.portal_fee)}</td>
                                <td className="px-3 py-2 text-right font-black text-slate-900 tabular-nums" data-testid="top-parties-total-combined">{inr(totals.net_amount)}</td>
                                <td className="px-3 py-2 text-right font-black text-slate-900 tabular-nums" data-testid="top-parties-total-count">{totals.txn_count}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SumCard({ label, value, testId, tint, strong }) {
    const ring = tint === "emerald" ? "border-emerald-200 bg-white"
        : tint === "violet" ? "border-violet-200 bg-white"
        : tint === "amber" ? "border-amber-200 bg-white"
        : "border-slate-300 bg-slate-900 text-white";
    const labelCls = tint === "slate"
        ? "text-white/80"
        : tint === "emerald" ? "text-emerald-700"
        : tint === "violet" ? "text-violet-700"
        : "text-amber-800";
    const valCls = tint === "slate"
        ? "text-white"
        : tint === "emerald" ? "text-emerald-800"
        : tint === "violet" ? "text-violet-800"
        : "text-amber-900";
    return (
        <div className={`rounded-lg border px-3 py-2.5 shadow-sm ${ring} ${strong ? "ring-1 ring-slate-900/10" : ""}`} data-testid={testId}>
            <div className={`text-[10px] font-bold uppercase tracking-wider ${labelCls}`}>{label}</div>
            <div className={`mt-1 text-lg font-black tabular-nums ${valCls}`}>{inr(value)}</div>
        </div>
    );
}
